import {ApiUrls} from 'shared/config/model/urls';

import {SubmitHandler, useForm, UseFormReturn} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';

import {tagsAPI, useBatchOperation, useBatchResult, IBatchResult} from 'entities/monitor';
import {portMonitorAPI} from 'features/port/api/portMonitorAPI';
import {buildPortBatchCreateRequest, getPortBatchCreateDefaults} from 'features/port/lib/portMonitorFormHelper';
import {useIsFreeTariff, fetchMe, userSelector} from 'entities/user';
import {TypedDispatch} from 'shared/model/types';

import BatchCreatePortMonitorType from './PortMonitor/BatchCreatePortMonitorType';

export interface IUsePortMonitorBatchCreateForm {
    form: UseFormReturn<BatchCreatePortMonitorType>,
    result: IBatchResult | undefined,
    isLoading: boolean,
    progress: number,
    submit: ReturnType<UseFormReturn<BatchCreatePortMonitorType>['handleSubmit']>,
    handleCloseResult: () => void,
}

export const usePortMonitorBatchCreateForm = (
    handleClose: () => void,
    onShowResultChange?: (_isShowing: boolean) => void,
): IUsePortMonitorBatchCreateForm => {
    const dispatch = useDispatch<TypedDispatch>();
    const user = useSelector(userSelector);
    const freeTariff = useIsFreeTariff();

    const {result, showResult, resetResult} = useBatchResult(onShowResultChange);
    const {trigger, isLoading, progress} = useBatchOperation();
    const {refetch: refetchTags} = tagsAPI.useGetTagsQuery();

    const form = useForm<BatchCreatePortMonitorType>({
        mode: 'onChange',
        defaultValues: getPortBatchCreateDefaults({freeTariff, emailConfirmed: !!user?.email_confirmed}),
    });

    const onSubmit: SubmitHandler<BatchCreatePortMonitorType> = async(values) => {
        await trigger(
            ApiUrls.PORT_MONITORS_BATCH_ACTION('create'),
            'POST',
            buildPortBatchCreateRequest(values),
            (batchResult) => {
                dispatch(portMonitorAPI.util.invalidateTags([
                    {type: 'PortMonitor', id: 'LIST'},
                    'SummaryPortMonitors',
                ]));
                refetchTags();
                dispatch(fetchMe());
                showResult(batchResult);
            },
        );
    };

    const handleCloseResult = () => {
        handleClose();
        resetResult();
    };

    return {
        form,
        result,
        isLoading,
        progress,
        submit: form.handleSubmit(onSubmit),
        handleCloseResult,
    };
};

export default usePortMonitorBatchCreateForm;
